import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { academicAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchAcademicYears = createAsyncThunk('academic/years', async (params, { rejectWithValue }) => {
  try {
    const response = await academicAPI.fetchAcademicYears(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch academic years'));
  }
});

export const createAcademicYear = createAsyncThunk('academic/createYear', async (data, { rejectWithValue }) => {
  try {
    const response = await academicAPI.createAcademicYear(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create academic year'));
  }
});

export const fetchTerms = createAsyncThunk('academic/terms', async (params, { rejectWithValue }) => {
  try {
    const response = await academicAPI.fetchTerms(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch terms'));
  }
});

export const createTerm = createAsyncThunk('academic/createTerm', async (data, { rejectWithValue }) => {
  try {
    const response = await academicAPI.createTerm(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create term'));
  }
});

export const fetchClasses = createAsyncThunk('academic/classes', async (params, { rejectWithValue }) => {
  try {
    const response = await academicAPI.fetchClasses(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch classes'));
  }
});

export const createClass = createAsyncThunk('academic/createClass', async (data, { rejectWithValue }) => {
  try {
    const response = await academicAPI.createClass(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create class'));
  }
});

export const updateClass = createAsyncThunk('academic/updateClass', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await academicAPI.updateClass(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update class'));
  }
});

export const deleteClass = createAsyncThunk('academic/deleteClass', async (id, { rejectWithValue }) => {
  try {
    await academicAPI.deleteClass(id);
    return id;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to delete class'));
  }
});

export const fetchStreams = createAsyncThunk('academic/streams', async (params, { rejectWithValue }) => {
  try {
    const response = await academicAPI.fetchStreams(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch streams'));
  }
});

export const createStream = createAsyncThunk('academic/createStream', async (data, { rejectWithValue }) => {
  try {
    const response = await academicAPI.createStream(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create stream'));
  }
});

export const fetchSubjects = createAsyncThunk('academic/subjects', async (params, { rejectWithValue }) => {
  try {
    const response = await academicAPI.fetchSubjects(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch subjects'));
  }
});

export const createSubject = createAsyncThunk('academic/createSubject', async (data, { rejectWithValue }) => {
  try {
    const response = await academicAPI.createSubject(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create subject'));
  }
});

export const updateSubject = createAsyncThunk('academic/updateSubject', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await academicAPI.updateSubject(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update subject'));
  }
});

export const deleteSubject = createAsyncThunk('academic/deleteSubject', async (id, { rejectWithValue }) => {
  try {
    await academicAPI.deleteSubject(id);
    return id;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to delete subject'));
  }
});

export const fetchTimetable = createAsyncThunk('academic/timetable', async (params, { rejectWithValue }) => {
  try {
    const response = await academicAPI.fetchTimetable(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch timetable'));
  }
});

export const createTimetableEntry = createAsyncThunk('academic/createTimetable', async (data, { rejectWithValue }) => {
  try {
    const response = await academicAPI.createTimetableEntry(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create timetable entry'));
  }
});

const initialState = {
  academicYears: [],
  terms: [],
  classes: [],
  streams: [],
  subjects: [],
  timetable: [],
  loading: false,
  error: null,
};

const academicSlice = createSlice({
  name: 'academic',
  initialState,
  reducers: {
    clearAcademicError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAcademicYears.pending, (state) => { state.loading = true; })
      .addCase(fetchAcademicYears.fulfilled, (state, action) => { state.loading = false; state.academicYears = action.payload.data || action.payload; })
      .addCase(fetchAcademicYears.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createAcademicYear.fulfilled, (state, action) => { state.academicYears.unshift(action.payload); })
      .addCase(fetchTerms.pending, (state) => { state.loading = true; })
      .addCase(fetchTerms.fulfilled, (state, action) => { state.loading = false; state.terms = action.payload.data || action.payload; })
      .addCase(fetchTerms.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createTerm.fulfilled, (state, action) => { state.terms.unshift(action.payload); })
      .addCase(fetchClasses.pending, (state) => { state.loading = true; })
      .addCase(fetchClasses.fulfilled, (state, action) => { state.loading = false; state.classes = action.payload.data || action.payload; })
      .addCase(fetchClasses.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createClass.fulfilled, (state, action) => { state.classes.unshift(action.payload); })
      .addCase(updateClass.fulfilled, (state, action) => { state.classes = state.classes.map((c) => c.id === action.payload.id ? action.payload : c); })
      .addCase(deleteClass.fulfilled, (state, action) => { state.classes = state.classes.filter((c) => c.id !== action.payload); })
      .addCase(fetchStreams.fulfilled, (state, action) => { state.streams = action.payload.data || action.payload; })
      .addCase(createStream.fulfilled, (state, action) => { state.streams.unshift(action.payload); })
      .addCase(fetchSubjects.pending, (state) => { state.loading = true; })
      .addCase(fetchSubjects.fulfilled, (state, action) => { state.loading = false; state.subjects = action.payload.data || action.payload; })
      .addCase(fetchSubjects.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createSubject.fulfilled, (state, action) => { state.subjects.unshift(action.payload); })
      .addCase(updateSubject.fulfilled, (state, action) => { state.subjects = state.subjects.map((s) => s.id === action.payload.id ? action.payload : s); })
      .addCase(deleteSubject.fulfilled, (state, action) => { state.subjects = state.subjects.filter((s) => s.id !== action.payload); })
      .addCase(fetchTimetable.pending, (state) => { state.loading = true; })
      .addCase(fetchTimetable.fulfilled, (state, action) => { state.loading = false; state.timetable = action.payload.data || action.payload; })
      .addCase(fetchTimetable.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createTimetableEntry.fulfilled, (state, action) => { state.timetable.push(action.payload); });
  },
});

export const { clearAcademicError } = academicSlice.actions;
export default academicSlice.reducer;
